import { Trans } from "@web-monorepo/i18n/macro";
import { IconLockClosed } from "@web-monorepo/dds-icons";
import { TranslatedString } from "@web-monorepo/translation-runtime";
import { clsx } from "clsx";
import { useCallback, useEffect } from "react";
import { Link } from "react-router-dom";
import type { ToolkitActivityVariant } from "./BeyondToolkit/activityVariant";
import { openSalesPage } from "src/pods/webviewInit";
import { logParentToolkitEvent } from "src/utils/logEvent";
import styles from "./ToolkitListItem.module.css";

type ToolkitListItemProps = {
  title?: TranslatedString;
  description?: string;
  pageUrl: string;
  // `variant` picks the activity's brand bg from `ToolkitListItem.module.css`.
  // See `BeyondToolkit/activityVariant.ts` for the full set.
  variant: ToolkitActivityVariant;
  imgUrl?: string;
  isLocked?: boolean;
  loggingId?: string;
  size?: "large" | "small";
  lockedUrl: string;
  isNew?: boolean;
};

export default function ToolkitListItem(props: ToolkitListItemProps) {
  const eventPrefix = `${props.loggingId}${props.isLocked ? "_locked" : ""}`;

  useEffect(() => {
    logParentToolkitEvent({ eventName: `${eventPrefix}.exposure` });
  }, [eventPrefix]);

  const onClick = useCallback(() => {
    logParentToolkitEvent({ eventName: `${eventPrefix}.tap` });
    if (props.isLocked) {
      openSalesPage();
    }
  }, [eventPrefix, props.isLocked]);

  const isLarge = props.size === "large";

  // The Link itself IS the tile — visual button = hit area. No wrapping
  // block-level container around an inline `<a>`.
  return (
    <Link
      to={props.isLocked ? props.lockedUrl : props.pageUrl}
      className={clsx(styles.tile, styles[props.variant], isLarge ? styles.tileLarge : styles.tileSmall)}
      onClick={onClick}
    >
      <div className={clsx(styles.textColumn, isLarge ? styles.textColumnLarge : styles.textColumnSmall)}>
        {props.isNew && (
          <>
            <div className={styles.newBadge}>
              <Trans>new</Trans>
            </div>
            <br />
          </>
        )}
        {props.isLocked && (
          <div className={styles.lockIcon}>
            <IconLockClosed />
          </div>
        )}
        <div className={styles.title}>{props.title}</div>
        <div className={clsx(styles.description, isLarge ? styles.descriptionLarge : styles.descriptionSmall)}>
          {props.description}
        </div>
      </div>
      <img src={props.imgUrl} alt="" className={styles.thumb} />
    </Link>
  );
}
